import { useTranslation } from 'next-i18next/pages';
import { OrganizationJsonLd } from 'next-seo';

import { SourceDomain } from '@pflegia/types/api/source-domain/source-domain';
import { useFlavorFromRoute } from '@pflegia/ui/hooks/use-flavor-from-route';
import { removeTrailingSlash } from '@pflegia/util/common/remove-trailing-slash';

import {
  CUSTOMER_SERVICE_TELEPHONE,
  FACEBOOK_URL,
  INSTAGRAM_URL,
  LINKEDIN_URL,
  PRAXIA_FACEBOOK_URL,
  PRAXIA_INSTAGRAM_URL,
  PRAXIA_LINKEDIN_URL,
  PRAXIA_YOUTUBE_URL,
  YOUTUBE_URL,
} from '../../application/constants';
import { useLogoUrl } from '../../hooks/use-logo-url';
import { getBaseUrlByHostName } from '../../utils/domain';

const PFLEGIA_FOUNDING_DATE = '2019';
const PRAXIA_FOUNDING_DATE = '2024';

const getSocialMediaUrls = (sourceDomain: SourceDomain) => {
  if (sourceDomain === SourceDomain.PraxiaDe) {
    return [PRAXIA_LINKEDIN_URL, PRAXIA_FACEBOOK_URL, PRAXIA_YOUTUBE_URL, PRAXIA_INSTAGRAM_URL];
  }
  return [FACEBOOK_URL, INSTAGRAM_URL, YOUTUBE_URL, LINKEDIN_URL];
};

const getFoundingDate = (sourceDomain: SourceDomain) => {
  if (sourceDomain === SourceDomain.PraxiaDe) return PRAXIA_FOUNDING_DATE;
  return PFLEGIA_FOUNDING_DATE;
};

const OrganizationJsonLdBase = () => {
  const { sourceDomain, sourceDomainName } = useFlavorFromRoute();
  const baseUrl = removeTrailingSlash(getBaseUrlByHostName(sourceDomain));
  const logoUrl = useLogoUrl();
  const { t } = useTranslation('common');
  return (
    <OrganizationJsonLd
      name={sourceDomainName}
      url={baseUrl}
      logo={logoUrl}
      foundingDate={getFoundingDate(sourceDomain)}
      description={t('seo.description', { sourceDomainName })}
      legalName={`${sourceDomainName} GmbH`}
      address={{
        '@type': 'PostalAddress',
        streetAddress: 'Chausseestraße 19',
        addressLocality: 'Berlin',
        postalCode: '10115',
        addressCountry: 'DE',
      }}
      sameAs={getSocialMediaUrls(sourceDomain)}
      contactPoint={[
        {
          telephone: CUSTOMER_SERVICE_TELEPHONE,
          contactType: 'Customer Support',
          areaServed: sourceDomain === SourceDomain.PflegiaAt ? 'AT' : 'DE',
          email: `kontakt@${sourceDomain}`,
          availableLanguage: ['German', 'English'],
        },
      ]}
    />
  );
};

export default OrganizationJsonLdBase;
